import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomTabs from '../../../components/custom-tabs/custom-tabs';
import useCompany from '../../../hooks/useCompany';
import { getDefaultCompanyDetails, COMPANY_DETAILS_TAB_LIST } from './company-details.utils';
import BasicInfo from './basic-info/basic-info';
import { CompanyDetailsStyled } from './company-details.styles';
import { companiesCollection } from '../../../services/firebase';
import { useAuth } from '../../../context/AuthContext';
import CompanyHeader from './components/company-header';
import { isSomething } from '../../../utils/utils';
import { Typography } from '@mui/material';
import { CreateCompanyValidations } from '../../../constants/validations';
import LinkedProjects from './linked-projects/linked-projects';

const CompanyDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('basic-info');
  const { userPermissions } = useAuth();
  const [currentCompanyData, setCurrentCompanyData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const { data: companyDetails, refetch } = useCompany(id);

  const companyData = getDefaultCompanyDetails(companyDetails);

  const updateCompanyData = (updatedData) => {
    setCurrentCompanyData(updatedData);
  };

  const form = useForm({
    mode: 'all',
    defaultValues: companyData,
    resolver: yupResolver(CreateCompanyValidations()),
  });

  const { reset } = form;

  useEffect(() => {
    if (isSomething(companyDetails)) {
      reset(companyData);
    }
  }, [companyDetails]);

  const deleteCompany = async (id) => {
    if (userPermissions === 'General') {
      alert('You do not have permission to delete!');
      return;
    }
    const isConfirmed = window.confirm(
      'Are you sure? If you delete this company, all the information will be lost.',
    );
    if (isConfirmed) {
      try {
        await companiesCollection.doc(id).delete();
        navigate('/companiesview/');
      } catch (error) {
        console.error('Error deleting:', error);
        alert('Failed to delete!');
      }
    }
  };

  const handleSave = form.handleSubmit(
    async (formData) => {
      try {
        setIsSaving(true);

        const updatedCompanyData = {
          companyName: formData.companyName,
          type: formData.type,
          rateCard: formData.rateCard,
          companyState: formData.companyState,
          webAddress: formData.webAddress,
          phoneNumber: formData.phoneNumber,
          streetAddress: formData.streetAddress,
          address: formData.address,
          city: formData.city,
          province: formData.province,
          postalCode: formData.postalCode,
          companyNotes: formData.companyNotes,
          isNotBilling: formData.isNotBilling,
          billingStreetAddress: formData.billingStreetAddress,
          billingAddress: formData.billingAddress,
          billingCity: formData.billingCity,
          billingProvince: formData.billingProvince,
          billingPostalCode: formData.billingPostalCode,
          billingCountry: formData.billingCountry,
        };

        await companiesCollection.doc(id).update(updatedCompanyData);
        refetch();
        updateCompanyData(updatedCompanyData);
        setIsEditMode(false);
      } catch (error) {
        console.error('Error updating company:', error);
      } finally {
        setIsSaving(false);
      }
    },
    (errors) => {
      // If there are errors, shake the save button.
      const saveButton = document.getElementById('saveButton');
      if (saveButton) {
        saveButton.classList.add('shake');
        setTimeout(() => saveButton.classList.remove('shake'), 500);
      }
    },
  );

  return (
    <CompanyDetailsStyled>
      <CompanyHeader
        isSaveEnabled={form?.formState?.isValid}
        companyDetails={companyDetails}
        activeTab={activeTab}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        isSaving={isSaving}
        handleSave={handleSave}
      />

      <div>
        <div className="box">
          <div className="details-wrapper">
            <CustomTabs
              activeTab={activeTab}
              handleChange={setActiveTab}
              tabsList={COMPANY_DETAILS_TAB_LIST}
            />
          </div>
          <div className="tab-content">
            {activeTab === 'basic-info' && (
              <BasicInfo
                refetch={refetch}
                companyData={companyData}
                updateCompanyData={updateCompanyData}
                isEditMode={isEditMode}
                form={form}
              />
            )}
            {activeTab === 'linked-projects' && (
              <div className="table-scroll-container">
                <LinkedProjects companyID={companyData.companyID} />
              </div>
            )}
          </div>
        </div>
        <div className="footer">
          <hr></hr>
          <div className="space-between">
            <div className="created">
              <Typography variant="body3">Created By: {companyData.createdBy}</Typography>
              <Typography variant="body3">Created Date: {companyData.dateCreated}</Typography>
            </div>
            {/* <div className="delete-icon">
              {userPermissions !== "General" && (
                <Button
                  endIcon={<DeleteIcon color="#fff" />}
                  onClick={() => deleteCompany(id)}
                  color={"error"}
                  variant={"filled"}
                >
                  Delete Company
                </Button>
              )}
            </div> */}
          </div>
        </div>
      </div>
    </CompanyDetailsStyled>
  );
};

export default CompanyDetails;
